import { useMemo } from "react"
import "./index.scss"
import { useSelector } from "react-redux"
import { DateTime } from "luxon"
import Table from "react-bootstrap/Table"
import useT from "utils/hooks/useT"

export default function LogTable({ log, viewType = "task", searchTerm = "" }) {
  const { t } = useT("common")
  const timeZone = useSelector((state) => state.teamInfo.timezone)

  // Ensure we don't mutate the redux state and handle non-array log values
  const sortedLogs = useMemo(() => {
    const logs = Array.isArray(log) ? [...log].sort((a, b) => new Date(b.editTime) - new Date(a.editTime)) : []

    // Filter by sample name if in sample view and search term exists
    if (viewType === "sample" && searchTerm) {
      return logs.filter((entry) => {
        const sampleName = entry.sampleName || entry.name || ""
        return sampleName.toLowerCase().includes(searchTerm.toLowerCase())
      })
    }

    return logs
  }, [log, viewType, searchTerm]);

  const formatDateTime = (dateString) => {
    if (!dateString) return t("dateUnavailable");
    const dt = DateTime.fromISO(dateString, { zone: 'utc' }).setZone(timeZone);
    return dt.isValid ? dt.toFormat("d. MMM HH.mm") : t("invalidDate");
  };

  function capitalizeFirstLetter(string) {
    if (!string) return ""
    return String(string).charAt(0).toUpperCase() + String(string).slice(1).toLowerCase();
  }

  return (
    <Table bordered hover className="log-table">
      <thead>
        <tr>
          <th>{t("edited")}</th>
          {viewType === "instrument" ? (
            <>
              <th>{t("oldInstrument")}</th>
              <th>{t("newInstrument")}</th>
              <th>{t("taskName")}</th>
              <th>{t("movedBy")}</th>
              <th>{t("oldStart")}</th>
              <th>{t("newStart")}</th>
            </>
          ) : viewType === "sample" ? (
            <>
              <th>{t("sampleName")}</th>
              <th>{t("sampleId")}</th>
              <th>{t("analysisName")}</th>
              <th>{t("movedBy")}</th>
              <th>{t("movedFrom")}</th>
              <th>{t("movedTo")}</th>
              <th>{t("oldStart")}</th>
              <th>{t("newStart")}</th>
              <th>{t("type")}</th>
            </>
          ) : (
            <>
              <th>{t("movedBy")}</th>
              <th>{t("name")}</th>
              <th>{t("movedFrom")}</th>
              <th>{t("movedTo")}</th>
              <th>{t("oldStart")}</th>
              <th>{t("newStart")}</th>
              <th>{t("taskName")}</th>
              <th>{t("type")}</th>
            </>
          )}
        </tr>
      </thead>
      <tbody>
        {viewType === "instrument" ? (
          sortedLogs.map((entry, index) => (
            <tr key={index}>
              <td className="log-table-time-column">{formatDateTime(entry.editTime)}</td>
              <td className="log-table-instrument">{entry.oldInstrument || t("none")}</td>
              <td className="log-table-instrument">{entry.newInstrument || t("none")}</td>
              <td className="log-table-task-name">{entry.taskName}</td>
              <td>{entry.movedByMember}</td>
              <td className="log-table-time-column">{entry.oldStart ? formatDateTime(entry.oldStart) : t("unplanned")}</td>
              <td className="log-table-time-column">{entry.newStart ? formatDateTime(entry.newStart) : t("unplanned")}</td>
            </tr>
          ))
        ) : viewType === "sample" ? (
          sortedLogs.map((entry, index) => (
            <tr key={index}>
              <td className="log-table-time-column">{formatDateTime(entry.editTime)}</td>
              <td className="log-table-name">{entry.sampleName || entry.name}</td>
              <td>{entry.sampleId}</td>
              <td>{entry.analysisName || entry.oldAnalysis || entry.newAnalysis || t("notAvailable")}</td>
              <td>{entry.movedByMember}</td>
              <td>{entry.movedFrom ? entry.movedFrom : t("unplanned")}</td>
              <td>{entry.movedTo ? entry.movedTo : t("unplanned")}</td>
              <td className="log-table-time-column">{entry.oldStart ? formatDateTime(entry.oldStart) : t("unplanned")}</td>
              <td className="log-table-time-column">{entry.newStart ? formatDateTime(entry.newStart) : t("unplanned")}</td>
              <td>{capitalizeFirstLetter(entry.type)}</td>
            </tr>
          ))
        ) : (
          sortedLogs.map((entry, index) => (
            <tr key={index}>
              <td className="log-table-time-column">{formatDateTime(entry.editTime)}</td>
              <td>{entry.movedByMember}</td>
              <td className="log-table-name">{entry.name}</td>
              <td>{entry.oldMember ? entry.oldMember : t("unplanned")}</td>
              <td>{entry.newMember ? entry.newMember : t("unplanned")}</td>
              <td className="log-table-time-column">{entry.oldStart ? formatDateTime(entry.oldStart) : t("unplanned")}</td>
              <td className="log-table-time-column">{entry.newStart ? formatDateTime(entry.newStart) : t("unplanned")}</td>
              <td className="log-table-task-name">{entry.taskName}</td>
              <td>{capitalizeFirstLetter(entry.type)}</td>
            </tr>
          ))
        )}
      </tbody>
    </Table>
  );
}
